import React from 'react';
import styled from 'styled-components';

import { formattedDateTime } from '../../../../../../utils/dateUtils';
import { TimelineContainer } from '../../../../../../viewer/timeline-manager';
import { Icon } from '../../../../icon/icon';
import { SkyviewContext } from '../../../../skyview/skyview-state';
import { Stack } from '../../../../stack/stack';
import { TimelineItem } from './timeline-item';

type Props = {
  timelineContainers: TimelineContainer[];
  currentTimelineContainer: TimelineContainer;
  maxVisibleItems: number;
};

const Timeline = (props: Props) => {
  const { skyview } = React.useContext(SkyviewContext);

  const currentIndex = props.timelineContainers.indexOf(props.currentTimelineContainer);
  const [scrollIndex, setScrollIndex] = React.useState(
    Math.max(0, currentIndex - Math.floor(props.maxVisibleItems / 2)),
  );
  const maxVisibleItems =
    props.timelineContainers.length < props.maxVisibleItems
      ? props.timelineContainers.length
      : props.maxVisibleItems;

  const isLastItemVisible = props.timelineContainers.length - scrollIndex - 1 < maxVisibleItems;
  const leftArrowEnabled = scrollIndex > 0;
  const rightArrowEnabled = !isLastItemVisible;
  const arrowsVisible = leftArrowEnabled || rightArrowEnabled;

  React.useEffect(() => {
    const spliceStart = isLastItemVisible
      ? props.timelineContainers.length - maxVisibleItems
      : scrollIndex;
    setScrollIndex(spliceStart);
  }, [props.timelineContainers, scrollIndex, isLastItemVisible, maxVisibleItems]);

  return (
    <Stack alignItems="center" direction="row" spacing="1em">
      {arrowsVisible && (
        <Icon
          color="#5e6a71"
          disabled={!leftArrowEnabled}
          icon={['fas', 'caret-left']}
          size="2x"
          onClick={() => {
            setScrollIndex((index) => index - 1);
          }}
          onHoverStyle={{ color: '#37424a' }}
        />
      )}
      <TimelineUiContainer direction="row" forwardedAs="ul" spacing="0.8em">
        {props.timelineContainers
          .slice(scrollIndex, scrollIndex + maxVisibleItems)
          .map((x, index) => {
            const containerIndex = scrollIndex + index;
            return (
              <TimelineItem
                key={x.id}
                selected={containerIndex === currentIndex}
                separators={{
                  width: '0.4em', // Half of space between items
                  leftVisible: containerIndex > 0,
                  rightVisible: containerIndex < props.timelineContainers.length - 1,
                }}
                tooltip={{
                  text: x.comment,
                  title: formattedDateTime(x.collectionDate),
                }}
                value={x.containerIndex}
                // The scene stays, so an open tool, such as a design being edited, stays open too
                onClick={() => skyview.setActiveContainer(x.id)}
              />
            );
          })}
      </TimelineUiContainer>
      {arrowsVisible && (
        <Icon
          color="#5e6a71"
          disabled={!rightArrowEnabled}
          icon={['fas', 'caret-right']}
          size="2x"
          onClick={() => {
            setScrollIndex((index) => index + 1);
          }}
          onHoverStyle={{ color: '#37424a' }}
        />
      )}
    </Stack>
  );
};

const TimelineUiContainer = styled(Stack)`
  isolation: isolate;
`;

export { Timeline, Props as TimelineProps };
